import { SearchOutlined } from '@mui/icons-material'
import {
  Autocomplete,
  AutocompleteProps,
  InputAdornment,
  ListItem,
  ListItemText,
  TextField,
} from '@mui/material'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import _, { isEqual } from 'lodash'
import React, { memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { WorkingStepType } from 'services/APIs/InternalAPI/internal-api.contracts'
import { markText } from 'utils/textFilterUtils'

const WorkingStepAutoComplete = memo(
  (props: {
    availableWorkingSteps: Array<WorkingStepType>
    onOptionSelected: (
      workingStepType: WorkingStepType,
      event: React.SyntheticEvent,
    ) => void
    AutoCompleteProps?: Partial<
      AutocompleteProps<WorkingStepType, false, true, false>
    >
    selectedOption?: WorkingStepType | null
    label?: string
    placeholder?: string
    required?: boolean
  }) => {
    const { t } = useTranslation()
    const getLocalizedWorkingStep = useLocalizedWorkingStepLabel()
    const { onOptionSelected } = props
    const [inputValue, setInputValue] = useState('')

    const handleInputChange = useCallback(
      (_event: React.SyntheticEvent, value: string, reason: string) => {
        if (reason === 'input') {
          setInputValue(value)
        } else if (reason === 'clear') {
          setInputValue('')
        }
        // ignore 'reset' — prevents MUI from filling the input with the selected label
      },
      [],
    )

    const sortedWorkingSteps = React.useMemo(() => {
      return props.availableWorkingSteps.sort((a, b) => {
        return getLocalizedWorkingStep(a).localeCompare(
          getLocalizedWorkingStep(b),
        )
      })
    }, [getLocalizedWorkingStep, props.availableWorkingSteps])

    const handleOptionSelected = useCallback(
      (
        event: React.SyntheticEvent,
        value: WorkingStepType | null,
        reason: string,
      ) => {
        if (reason === 'selectOption' && value) {
          onOptionSelected(value, event)
        }
      },
      [onOptionSelected],
    )

    return (
      <Autocomplete
        value={props.selectedOption || ({} as WorkingStepType)}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleOptionSelected}
        openOnFocus
        options={sortedWorkingSteps || []}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              name="workingStep"
              variant="standard"
              slotProps={{
                input: {
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined color="action" />
                    </InputAdornment>
                  ),
                },
              }}
              placeholder={
                props.placeholder || t('project:working-steps-available')
              }
              label={props.label}
              required={props.required}
            />
          )
        }}
        renderOption={(baseElProps, option, state) => {
          return (
            <ListItem
              disableGutters
              {...baseElProps}
              key={option}
              data-value={option}
            >
              <WorkingStepIconSvg
                attributes={{
                  width: '32px',
                }}
                workingStepType={option}
              />
              <ListItemText
                style={{
                  paddingLeft: '16px',
                }}
                primary={markText(
                  getLocalizedWorkingStep(option),
                  state.inputValue,
                )}
              />
            </ListItem>
          )
        }}
        getOptionLabel={(option) => {
          if (WorkingStepType[option]) {
            return getLocalizedWorkingStep(option)
          } else {
            if (_.isEmpty(option)) {
              return ''
            }
            return option
          }
        }}
        {...props.AutoCompleteProps}
      />
    )
  },
  isEqual,
)

WorkingStepAutoComplete.displayName = 'WorkingStepAutoComplete'

export default WorkingStepAutoComplete
